<!-- ===========================================================
     Slide-in subtask sidebar (right edge of viewport).
     The backdrop captures clicks outside the panel and closes
     the sidebar; the panel itself uses a CSS translateX
     transition driven by the `.open` class.
     =========================================================== -->

<div class="sp-backdrop" [class.open]="isOpen" (click)="onClose()"></div>

<div class="sp-panel" [class.open]="isOpen">

  <!-- ============== Header ============== -->
  <div class="sp-header" *ngIf="task">
    <div class="sp-header-left">
      <div class="sp-breadcrumb">Case Board &rsaquo; Child Cases</div>
      <div class="sp-parent-name">{{ task.taskName }}</div>
      <div class="sp-parent-meta">
        <span class="sp-meta-pill" [ngClass]="'status-' + parentStatusKey">
          {{ getStatusLabel(task.status) }}
        </span>
        <span class="sp-meta-pill" [ngClass]="'priority-' + parentPriorityKey">
          {{ getPriorityLabel(task.priorityLevel) }}
        </span>
        <span class="sp-meta-due" *ngIf="task.endDate" [class.overdue]="isParentOverdue()">
          Due {{ formatDate(task.endDate) }}<span *ngIf="isParentOverdue()"> · Overdue</span>
        </span>
      </div>
    </div>
    <button class="sp-close-btn" type="button" (click)="onClose()" matTooltip="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Subtle indicator while the parent refreshes the task header from API.
       Not a blocking overlay — cached data is already visible above. -->
  <div class="sp-refreshing" *ngIf="isRefreshing">
    <mat-spinner diameter="12"></mat-spinner>
    <span>Refreshing…</span>
  </div>

  <!-- ============== Actions bar ============== -->
  <div class="sp-actions" *ngIf="task">
    <!-- Uses the same `.scraii-primary-btn` class as the main task dialog's
         primary action button (defined in src/app/scss/partials/buttons.scss,
         globally imported via styles.scss → base.scss). -->
    <button type="button" class="sp-add-btn scraii-primary-btn" (click)="openSubtaskCreate()">
      + Add Child Case
    </button>
    <span class="sp-spacer"></span>
    <!-- Uses the same primitive-binding pattern as the main kanban card (see
         task-management-wf.component.html:125). The two getters in the .ts file
         compute counts from `this.subtasks` via the dynamic taskService.isDoneStatus
         resolver, so the bar updates on every CD tick after drag-drop / create / edit. -->
    <span class="sp-stat" *ngIf="subtasks.length">
      <app-task-progress-indicator [totalCount]="subtaskTotalCount" [doneCount]="subtaskDoneCount" [compact]="true">
      </app-task-progress-indicator>
    </span>
  </div>

  <!-- ============== Loading state ============== -->
  <div *ngIf="isLoadingSubtasks" class="sp-loading">
    <mat-spinner diameter="20"></mat-spinner>
    <span>Loading Child Cases…</span>
  </div>

  <!-- ============== Mini Kanban ============== -->
  <div *ngIf="!isLoadingSubtasks && task" class="sp-board">
    <app-subtask-mini-kanban [parentTask]="task" [externalSubtasks]="subtasks" [taskTypeOptions]="taskTypeOptions"
      (subtaskMoved)="onSubtaskMoved($event)" (subtaskClicked)="onSubtaskCardClicked($event)"
      (createSubtask)="openSubtaskCreateInColumn($event)" (subtaskCommentsClicked)="onSubtaskCommentsClicked($event)"
      (subtaskAttachmentsClicked)="onSubtaskAttachmentsClicked($event)">
    </app-subtask-mini-kanban>
  </div>

</div>

<!-- ===========================================================
     Subtask create/edit modal (overlays the sidebar).
     =========================================================== -->
<div class="st-overlay" [class.open]="showSubtaskDialog" (click)="onOverlayClick($event)">
  <div class="st-modal" (click)="$event.stopPropagation()">

    <div class="st-modal-header">
      <div class="st-modal-header-top">
        <span class="st-modal-title">{{ editingSubtask ? 'Edit Child Case' : 'Add Child Case' }}</span>
        <!-- Pill-toggle tabs -->
        <span class="scraii-task-mgmt-dialog-widContainers">
          <div class="d-flex justify-content-between">
            <button type="button" class="scraii-task-mgmt-dialog-cycleHover"
              [ngClass]="{ 'scraii-task-mgmt-dialog-highlight': subtaskActiveTab === 'details' }"
              (click)="onSubtaskTabChange('details')">
              Details
            </button>
            <button type="button" class="scraii-task-mgmt-dialog-cycleHover"
              [ngClass]="{ 'scraii-task-mgmt-dialog-highlight': subtaskActiveTab === 'updates' }"
              [disabled]="!editingSubtask" (click)="onSubtaskTabChange('updates')">
              Updates
              <span *ngIf="editingSubtask && subtaskCommentCount > 0" class="st-tab-count">
                {{ subtaskCommentCount }}
              </span>
            </button>
            <button type="button" class="scraii-task-mgmt-dialog-cycleHover"
              [ngClass]="{ 'scraii-task-mgmt-dialog-highlight': subtaskActiveTab === 'attachments' }"
              [disabled]="!editingSubtask" (click)="onSubtaskTabChange('attachments')">
              Attachments
              <span *ngIf="editingSubtask && subtaskAttachmentCount > 0" class="st-tab-count">
                {{ subtaskAttachmentCount }}
              </span>
            </button>
          </div>
        </span>
        <button class="st-modal-close" type="button" (click)="closeSubtaskDialog()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <div class="st-parent-pill" *ngIf="task">
      <mat-icon class="st-pill-icon">subdirectory_arrow_right</mat-icon>
      <span>{{ task.taskName }}</span>
    </div>

    <!-- Parent task tags (read-only) -->
    <div class="st-parent-tags" *ngIf="parentTags.length > 0">
      <span class="st-parent-tag-category" *ngIf="parentTagCategoryName">
        {{ parentTagCategoryName }}
      </span>
      <span *ngFor="let tag of parentTags" class="st-parent-tag-chip" [ngClass]="{
          'st-tag-patient': (tag.category_name || '').toUpperCase() === 'PATIENT',
          'st-tag-prescriber': (tag.category_name || '').toUpperCase() === 'PRESCRIBER',
          'st-tag-soc': (tag.category_name || '').toUpperCase() === 'SOC'
        }">
        {{ tag.tag_name }}
      </span>
    </div>

    <!-- Scrollable body — sits between the fixed header and the pinned footer.
         Contains whichever tab's content is active. -->
    <div class="st-modal-body" #stModalBody id="task_mgmt_id">

      <form [formGroup]="subtaskForm" class="st-form" *ngIf="subtaskActiveTab === 'details'">

        <!-- Row 1: Subtask Name + Task Type -->
        <div class="d-flex mt-2 flex-wrap">
          <div class="col-12 col-md-6 px-md-1 mb-2 mb-md-0">
            <mat-label>Child Case Name</mat-label>
            <mat-form-field appearance="outline" class="full-width">
              <input matInput formControlName="name" placeholder="Enter Child Case name…" />
            </mat-form-field>
          </div>
          <div class="col-12 col-md-6 px-md-1">
            <mat-label>Case Type</mat-label>
            <mat-form-field appearance="outline" class="full-width">
              <mat-select formControlName="taskType">
                <!-- <mat-option [value]="null">— select —</mat-option> -->
                <mat-option *ngFor="let t of taskTypeOptions" [value]="t.value">{{ t.label }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <!-- Row 2: Description -->
        <div class="d-flex mt-2">
          <div class="col-12 px-md-1">
            <mat-label>Description</mat-label>
            <mat-form-field appearance="outline" class="full-width">
              <textarea matInput rows="3" formControlName="description" placeholder="Child Case description…"></textarea>
            </mat-form-field>
          </div>
        </div>

        <!-- Row 3: Status + Priority (colored dot triggers matching main task dialog) -->
        <div class="d-flex mt-2 flex-wrap">
          <div class="col-12 col-md-6 px-md-1 mb-2 mb-md-0">
            <mat-label>Status</mat-label>
            <mat-form-field appearance="outline" class="full-width scraii-task-mgmt-dialog-status-pill-field">
              <mat-select formControlName="status">
                <mat-select-trigger>
                  <span class="scraii-task-mgmt-dialog-status-dot"
                    [ngClass]="statusKeyFor(subtaskForm.get('status')?.value)"></span>
                  <span class="scraii-task-mgmt-dialog-status-pill-text">
                    {{ getSubtaskStatusLabel(subtaskForm.get('status')?.value) }}
                  </span>
                </mat-select-trigger>
                <mat-option *ngFor="let s of statusOptions" [value]="s.value">
                  <span class="scraii-task-mgmt-dialog-status-dot" [ngClass]="statusKeyFor(s.value)"></span>
                  {{ optionLabelOf(s) }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div class="col-12 col-md-6 px-md-1">
            <mat-label>Priority</mat-label>
            <mat-form-field appearance="outline" class="full-width scraii-task-mgmt-dialog-priority-pill-field">
              <mat-select formControlName="priorityLevel">
                <mat-select-trigger>
                  <span class="scraii-task-mgmt-dialog-priority-dot"
                    [ngClass]="priorityKeyFor(subtaskForm.get('priorityLevel')?.value)"></span>
                  <span class="scraii-task-mgmt-dialog-priority-pill-text">
                    {{ getSubtaskPriorityLabel(subtaskForm.get('priorityLevel')?.value) }}
                  </span>
                </mat-select-trigger>
                <mat-option *ngFor="let p of priorityOptions" [value]="p.value">
                  <span class="scraii-task-mgmt-dialog-priority-dot" [ngClass]="priorityKeyFor(p.value)"></span>
                  {{ optionLabelOf(p) }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <!-- Row 4: Start Date + End Date -->
        <div class="d-flex mt-2 flex-wrap">
          <div class="col-12 col-md-6 px-md-1 mb-2 mb-md-0">
            <mat-label>Start Date</mat-label>
            <mat-form-field appearance="outline" class="full-width">
              <input matInput [matDatepicker]="stStartPicker" formControlName="startDate" placeholder="Start date">
              <mat-datepicker-toggle matSuffix [for]="stStartPicker">
                <mat-icon matDatepickerToggleIcon>
                  <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12.75 6.08333H0.75M9.41667 0.75V3.41667M4.08333 0.75V3.41667M3.95 14.0833H9.55C10.6701 14.0833 11.2302 14.0833 11.658 13.8653C12.0343 13.6736 12.3403 13.3676 12.532 12.9913C12.75 12.5635 12.75 12.0034 12.75 10.8833V5.28333C12.75 4.16323 12.75 3.60318 12.532 3.17535C12.3403 2.79903 12.0343 2.49307 11.658 2.30132C11.2302 2.08333 10.6701 2.08333 9.55 2.08333H3.95C2.8299 2.08333 2.26984 2.08333 1.84202 2.30132C1.46569 2.49307 1.15973 2.79903 0.967987 3.17535C0.75 3.60318 0.75 4.16323 0.75 5.28333V10.8833C0.75 12.0034 0.75 12.5635 0.967987 12.9913C1.15973 13.3676 1.46569 13.6736 1.84202 13.8653C2.26984 14.0833 2.8299 14.0833 3.95 14.0833Z"
                      stroke="#1363DF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </mat-icon>
              </mat-datepicker-toggle>
              <mat-datepicker #stStartPicker></mat-datepicker>
            </mat-form-field>
          </div>
          <div class="col-12 col-md-6 px-md-1">
            <mat-label>End Date</mat-label>
            <mat-form-field appearance="outline" class="full-width">
              <input matInput [matDatepicker]="stEndPicker" formControlName="endDate" placeholder="End date">
              <mat-datepicker-toggle matSuffix [for]="stEndPicker">
                <mat-icon matDatepickerToggleIcon>
                  <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12.75 6.08333H0.75M9.41667 0.75V3.41667M4.08333 0.75V3.41667M3.95 14.0833H9.55C10.6701 14.0833 11.2302 14.0833 11.658 13.8653C12.0343 13.6736 12.3403 13.3676 12.532 12.9913C12.75 12.5635 12.75 12.0034 12.75 10.8833V5.28333C12.75 4.16323 12.75 3.60318 12.532 3.17535C12.3403 2.79903 12.0343 2.49307 11.658 2.30132C11.2302 2.08333 10.6701 2.08333 9.55 2.08333H3.95C2.8299 2.08333 2.26984 2.08333 1.84202 2.30132C1.46569 2.49307 1.15973 2.79903 0.967987 3.17535C0.75 3.60318 0.75 4.16323 0.75 5.28333V10.8833C0.75 12.0034 0.75 12.5635 0.967987 12.9913C1.15973 13.3676 1.46569 13.6736 1.84202 13.8653C2.26984 14.0833 2.8299 14.0833 3.95 14.0833Z"
                      stroke="#1363DF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </mat-icon>
              </mat-datepicker-toggle>
              <mat-datepicker #stEndPicker></mat-datepicker>
            </mat-form-field>
          </div>
        </div>

        <!-- Row 5: Remind Me -->
        <div class="d-flex mt-2 flex-wrap">
          <div class="col-12 px-md-1">
            <div class="scraii-task-mgmt-dialog-remind-label-row mt-0">
              <mat-label>Remind Me</mat-label>
              <mat-slide-toggle formControlName="remindMe"></mat-slide-toggle>
            </div>
            <div class="remind-fields-row" *ngIf="subtaskForm.get('remindMe')?.value">
              <div class="remind-field-col">
                <!-- <label class="st-remind-lbl">Date</label> -->
                <mat-form-field appearance="outline" class="full-width">
                  <input matInput [matDatepicker]="stRemindPicker" [min]="remindMinDate" formControlName="remindDate"
                    placeholder="Pick date">
                  <mat-datepicker-toggle matSuffix [for]="stRemindPicker">
                    <mat-icon matDatepickerToggleIcon>
                      <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12.75 6.08333H0.75M9.41667 0.75V3.41667M4.08333 0.75V3.41667M3.95 14.0833H9.55C10.6701 14.0833 11.2302 14.0833 11.658 13.8653C12.0343 13.6736 12.3403 13.3676 12.532 12.9913C12.75 12.5635 12.75 12.0034 12.75 10.8833V5.28333C12.75 4.16323 12.75 3.60318 12.532 3.17535C12.3403 2.79903 12.0343 2.49307 11.658 2.30132C11.2302 2.08333 10.6701 2.08333 9.55 2.08333H3.95C2.8299 2.08333 2.26984 2.08333 1.84202 2.30132C1.46569 2.49307 1.15973 2.79903 0.967987 3.17535C0.75 3.60318 0.75 4.16323 0.75 5.28333V10.8833C0.75 12.0034 0.75 12.5635 0.967987 12.9913C1.15973 13.3676 1.46569 13.6736 1.84202 13.8653C2.26984 14.0833 2.8299 14.0833 3.95 14.0833Z"
                          stroke="#1363DF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </mat-icon>
                  </mat-datepicker-toggle>
                  <mat-datepicker #stRemindPicker></mat-datepicker>
                </mat-form-field>
              </div>
              <div class="remind-field-col">
                <!-- <label class="st-remind-lbl">Time</label> -->
                <mat-form-field appearance="outline" class="full-width">
                  <mat-select formControlName="remindTime" placeholder="Pick time">
                    <mat-option *ngFor="let opt of subtaskRemindTimeOptions; trackBy: trackSubtaskRemindOpt"
                      [value]="opt.value" [disabled]="disabledSubtaskRemindTimes.has(opt.value)">
                      {{ opt.label }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
                <div class="field-error" *ngIf="isSubtaskRemindTimeInPast">
                  Selected time is in the past. Pick a later slot or change the date.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 6: Assign To — searchable user dropdown -->
        <div class="d-flex mt-2 flex-wrap">
          <div class="col-12 px-md-1">
            <mat-label>Assign To</mat-label>
            <div class="assignee-search-wrap">
              <input class="st-assignee-input w-100" type="text" placeholder="Search user by name..." autocomplete="off"
                [(ngModel)]="subtaskAssigneeInput" [ngModelOptions]="{ standalone: true }"
                (focus)="onSubtaskAssigneeSearchFocus()" (input)="onSubtaskAssigneeSearchInput($event)"
                (blur)="onSubtaskAssigneeSearchBlur()" />
            </div>

            <div class="assignee-results-dropdown" [class.open]="showSubtaskAssigneeResults">
              <div *ngIf="isSearchingSubtaskUsers" class="assignee-results-loading">
                Loading users…
              </div>
              <div
                *ngIf="!isSearchingSubtaskUsers && subtaskAssigneeSearchResults.length === 0 && showSubtaskAssigneeResults"
                class="assignee-results-empty">
                No users found
              </div>
              <div *ngFor="let user of subtaskAssigneeSearchResults" class="assignee-result-item"
                [class.already-added]="isSubtaskUserAlreadySelected(user)"
                (mousedown)="$event.preventDefault(); onSubtaskUserResultSelect(user)">
                <div class="assignee-result-body">
                  <div class="assignee-result-name">{{ user.usr_display_nm || user.user_id }}</div>
                  <div class="assignee-result-sub">{{ user.user_id }}</div>
                </div>
                <span *ngIf="isSubtaskUserAlreadySelected(user)" class="assignee-result-check">&#10003;</span>
                <button *ngIf="!isSubtaskUserAlreadySelected(user)" class="assignee-result-add-btn"
                  type="button">+</button>
              </div>
            </div>

            <div class="assignee-chips" *ngIf="getSubtaskAssignees().length > 0">
              <div *ngFor="let email of getSubtaskAssignees(); let i = index" class="assignee-chip">
                <span>{{ getSubtaskAssigneeDisplayName(email) }}</span>
                <button type="button" class="assignee-chip-x" (click)="removeSubtaskAssignee(i)">×</button>
              </div>
            </div>
            <div class="field-error"
              *ngIf="subtaskForm.get('assignees')?.touched && subtaskForm.get('assignees')?.hasError('minArrayLength')">
              At least one assignee is required
            </div>
          </div>
        </div>

      </form>

      <!-- ====================================================
         Updates tab — comments/activity API. Lazy-loaded on
         tab click via onSubtaskTabChange('updates'). Disabled
         in create mode (subtask has no id yet).
         ==================================================== -->
      <div class="st-updates" *ngIf="subtaskActiveTab === 'updates'">

        <div *ngIf="isLoadingSubtaskComments" class="updates-loading">
          <mat-spinner diameter="20"></mat-spinner>
          <span>Loading comments…</span>
        </div>

        <div *ngIf="!isLoadingSubtaskComments && subtaskComments.length === 0" class="updates-empty">
          No comments yet — add the first one below.
        </div>

        <div class="updates-list" *ngIf="!isLoadingSubtaskComments && subtaskComments.length > 0">
          <div *ngFor="let comment of subtaskComments" class="update-entry">
            <div class="update-avatar">{{ getCommentInitials(comment.created_by) }}</div>
            <div class="update-content">
              <div class="update-meta">
                <span class="update-author">{{ comment.created_by }}</span>
                <span class="update-time" [matTooltip]="getFullTimestamp(comment.created_time)">
                  {{ getRelativeTime(comment.created_time) }}
                </span>
              </div>

              <div class="update-text-wrap" *ngIf="editingSubtaskCommentId !== comment.task_update_id">
                <div class="update-text">{{ comment.comment_text }}</div>
                <div *ngIf="comment.attachments?.length > 0" class="update-attachments">
                  <div *ngFor="let att of comment.attachments" class="update-att-chip">
                    <mat-icon class="att-icon">attach_file</mat-icon>
                    <span>{{ att.file_name || att.name || 'Attachment' }}</span>
                  </div>
                </div>
              </div>

              <div class="update-edit-wrap" *ngIf="editingSubtaskCommentId === comment.task_update_id">
                <textarea class="update-edit-ta" [(ngModel)]="editingSubtaskCommentText"
                  [ngModelOptions]="{ standalone: true }" rows="3"
                  (keydown.escape)="cancelEditSubtaskComment()"></textarea>
                <div class="update-edit-actions">
                  <button type="button" class="scraii-secondary-btn"
                    (click)="cancelEditSubtaskComment()">Cancel</button>
                  <button type="button" class="scraii-primary-btn" (click)="saveEditSubtaskComment(comment)"
                    [disabled]="!editingSubtaskCommentText.trim() || isSavingSubtaskEdit">
                    {{ isSavingSubtaskEdit ? 'Saving…' : 'Save' }}
                  </button>
                </div>
              </div>

              <div class="update-actions" *ngIf="editingSubtaskCommentId !== comment.task_update_id">
                <button type="button" class="update-action-btn" (click)="startEditSubtaskComment(comment)">Edit</button>
                <button type="button" class="update-action-btn delete" (click)="deleteSubtaskComment(comment)"
                  [disabled]="isDeletingSubtaskCommentId === comment.task_update_id">
                  {{ isDeletingSubtaskCommentId === comment.task_update_id ? 'Deleting…' : 'Delete' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Composer moved to the pinned footer below — it no longer scrolls
           with the comments. -->
      </div>

      <!-- ====================================================
         Attachments tab — lazy-loaded on first click via
         onSubtaskTabChange('attachments'). Disabled in create
         mode (subtask has no id yet — tab button disabled
         above). Identical visual language to the main task
         dialog's Attachments tab for consistency.
         ==================================================== -->
      <div class="st-attachments" *ngIf="subtaskActiveTab === 'attachments'">

        <!-- Loading state -->
        <div *ngIf="isLoadingSubtaskAttachments" class="att-loading">
          <mat-spinner diameter="20"></mat-spinner>
          <span>Loading attachments…</span>
        </div>

        <!-- Drop zone (only when we have a saved subtask) -->
        <div *ngIf="!isLoadingSubtaskAttachments && editingSubtask" class="att-drop-zone"
          [class.drag-over]="isSubtaskDragOver" (dragover)="onSubtaskDragOver($event)"
          (dragleave)="onSubtaskDragLeave($event)" (drop)="onSubtaskFileDrop($event)" (click)="stAttFileInput.click()">
          <input #stAttFileInput type="file" multiple hidden (change)="onSubtaskAttachmentInputChange($event)" />
          <mat-icon class="att-drop-icon">cloud_upload</mat-icon>
          <div class="att-drop-title">Click or drag &amp; drop files here</div>
          <div class="att-drop-sub">Attach documents, images, spreadsheets, PDFs</div>
          <div class="att-drop-limit">Max 50 MB per file</div>
        </div>

        <!-- Upload progress -->
        <div *ngIf="isUploadingSubtaskFile" class="att-upload-progress">
          <mat-progress-bar mode="determinate" [value]="subtaskUploadProgress"></mat-progress-bar>
          <span class="att-upload-label">Uploading… {{ subtaskUploadProgress }}%</span>
        </div>

        <!-- File list -->
        <div *ngIf="!isLoadingSubtaskAttachments && subtaskAttachments.length > 0" class="att-list">
          <div class="att-section-title">Files ({{ subtaskAttachments.length }})</div>
          <div *ngFor="let att of subtaskAttachments" class="att-item">
            <div class="att-item-thumb">
              <mat-icon>{{ getFileIcon(att.file_name) }}</mat-icon>
            </div>
            <div class="att-item-info">
              <div class="att-item-name" [matTooltip]="att.file_name">{{ att.file_name }}</div>
              <div class="att-item-meta">
                <span *ngIf="att.file_size">{{ formatFileSize(att.file_size) }}</span>
                <span *ngIf="att.uploaded_by">by {{ att.uploaded_by }}</span>
                <span *ngIf="att.uploaded_time" [matTooltip]="getFullTimestamp(att.uploaded_time)">
                  {{ getRelativeTime(att.uploaded_time) }}
                </span>
              </div>
            </div>
            <div class="att-item-actions">
              <button type="button" class="att-action-btn" (click)="downloadSubtaskAttachment(att)"
                matTooltip="Download">
                <mat-icon>download</mat-icon>
              </button>
              <button type="button" class="att-action-btn delete" (click)="deleteSubtaskAttachment(att)"
                [disabled]="isDeletingSubtaskAttachmentId === att.attachment_id" matTooltip="Delete">
                <mat-icon>{{ isDeletingSubtaskAttachmentId === att.attachment_id ? 'hourglass_empty' : 'delete_outline'
                  }}</mat-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty state (loaded but no files yet) -->
        <div *ngIf="!isLoadingSubtaskAttachments && editingSubtask && subtaskAttachments.length === 0"
          class="att-empty">
          <mat-icon class="att-empty-icon">folder_open</mat-icon>
          <p>No attachments yet — drop files above to upload.</p>
        </div>

      </div>
    </div><!-- /.st-modal-body -->

    <!-- Context-sensitive footer — always visible, shows different content
         per tab. Pinned below the scrollable .st-modal-body via flex layout. -->
    <div class="st-modal-footer">

      <!-- === Details tab: Cancel + Save === -->
      <ng-container *ngIf="subtaskActiveTab === 'details'">
        <button type="button" class="scraii-secondary-btn" (click)="closeSubtaskDialog()">
          Cancel
        </button>
        <button type="button" class="scraii-primary-btn st-save-btn" (click)="saveSubtask()"
          [disabled]="subtaskForm.invalid || isSavingSubtask">
          <span class="st-save-btn-label" [style.visibility]="isSavingSubtask ? 'hidden' : 'visible'">
            {{ editingSubtask ? 'Update Child Case' : 'Add Child Case' }}
          </span>
          <mat-spinner *ngIf="isSavingSubtask" diameter="16" class="st-save-btn-spinner">
          </mat-spinner>
        </button>
      </ng-container>

      <!-- === Updates tab: Comment composer === -->
      <ng-container *ngIf="subtaskActiveTab === 'updates'">
        <div class="footer-composer">
          <div class="footer-composer-input-wrap">
            <textarea class="footer-composer-input" [formControl]="$any(subtaskUpdateForm.controls['message'])"
              placeholder="Write a comment... (Shift+Enter for new line)" rows="1" maxlength="2000"
              (input)="onSubtaskComposerInput($event)" (keydown)="onSubtaskComposerKeydown($event)">
            </textarea>
          </div>
          <button type="button" class="scraii-primary-btn mb-2 footer-composer-btn"
            [disabled]="!subtaskUpdateForm.get('message')?.value?.trim() || isPostingSubtaskComment"
            (click)="onAddSubtaskUpdate()">
            <span [style.visibility]="isPostingSubtaskComment ? 'hidden' : 'visible'">Add Update</span>
            <mat-spinner *ngIf="isPostingSubtaskComment" diameter="14" class="footer-composer-spinner"></mat-spinner>
          </button>
        </div>
      </ng-container>

      <!-- === Attachments tab: no footer content (upload zone is inline) === -->
    </div>

  </div>
</div>